import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import SectionCard from "@/components/expediente/SectionCard";
import { ChatCircleText, Copy, Check, PaperPlaneTilt, CalendarBlank } from "@phosphor-icons/react";

/**
 * Tarjeta "Notificación a la aseguradora".
 * Muestra el texto generado a partir de la cita prevista del expediente
 * (plantilla configurada en Aseguradoras → {aseguradora} → Notif. citas).
 *
 * - Si NO hay visita_prevista_at: placeholder pidiendo programar la visita.
 * - Si la hay: textarea editable + botón "Copiar al portapapeles".
 * - Si la aseguradora está en modo "email": botón adicional "Enviar email"
 *   (sólo activo si hay `to` configurado).
 *
 * El texto editado es local (no se persiste): cada vez que cambien los datos
 * del expediente, se regenera. Si quieres una versión personalizada
 * permanente, edita la plantilla de la aseguradora.
 */
export default function CitaNotificacionCard({ exp, onOpenVisita }) {
  const [preview, setPreview] = useState(null);
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [sending, setSending] = useState(false);

  // visita_prevista_at + perito asignado son las dos cosas que pueden cambiar
  // y obligan a regenerar. Si cambian las usamos como key del efecto.
  const visitaIso = exp?.visita_prevista_at || "";
  const peritoCodigo = exp?.extracted_data?.perito_codigo || "";

  useEffect(() => {
    if (!exp?.id) return;
    let cancel = false;
    setLoading(true);
    api.get(`/expedientes/${exp.id}/cita/preview`)
      .then((r) => {
        if (cancel) return;
        setPreview(r.data);
        setBody(r.data?.body || "");
      })
      .catch(() => { if (!cancel) setPreview(null); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [exp?.id, visitaIso, peritoCodigo]);

  const copyToClipboard = async () => {
    if (!body) return;
    try {
      await navigator.clipboard.writeText(body);
      setCopied(true);
      toast.success("Texto copiado");
      setTimeout(() => setCopied(false), 2500);
    } catch (err) {
      toast.error("No se pudo copiar — selecciona el texto manualmente");
    }
  };

  const sendEmail = async () => {
    if (!preview?.to) {
      toast.error("Esta aseguradora aún no tiene email destino configurado");
      return;
    }
    setSending(true);
    try {
      await api.post(`/expedientes/${exp.id}/cita/notify`, {
        // re-usamos el endpoint: pasamos fecha/hora del expediente para que
        // no genere un nuevo evento si idempotente, y un body custom no es
        // soportado por ahora (envía el body de plantilla).
        fecha: visitaIso.slice(0, 10),
        hora_inicio: visitaIso.slice(11, 16),
        hora_fin: (exp.visita_prevista_fin || "").slice(11, 16),
        send: true,
        schedule_in_calendar: false,
      });
      toast.success(`Email enviado a ${preview.to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo enviar el email");
    } finally {
      setSending(false);
    }
  };

  const hasVisita = !!preview?.has_visita;

  return (
    <SectionCard
      icon={ChatCircleText}
      tone="emerald"
      title="Notificación a la aseguradora"
      testid="section-cita-notificacion"
    >
      {loading && (
        <div className="text-xs text-muted">Generando…</div>
      )}

      {!loading && !hasVisita && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-900 flex items-start gap-2" data-testid="cita-card-no-visita">
          <CalendarBlank className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-700" weight="fill" />
          <div className="flex-1">
            <div className="font-semibold">Aún no has programado la visita.</div>
            <div className="mt-0.5">Programa una fecha y hora para generar el texto a enviar a la aseguradora.</div>
            {onOpenVisita && (
              <button
                type="button"
                onClick={onOpenVisita}
                data-testid="cita-card-programar"
                className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold text-orange-700 hover:text-orange-800 underline underline-offset-2"
              >
                <CalendarBlank className="w-3 h-3" weight="bold" /> Programar visita prevista
              </button>
            )}
          </div>
        </div>
      )}

      {!loading && hasVisita && (
        <div className="space-y-2">
          <div className="text-[11px] text-muted">
            Texto generado a partir de la plantilla de <b>{preview?.aseguradora || "la aseguradora"}</b>
            {preview?.mode === "copy" && " (copiar y pegar en su portal)"}
            {preview?.mode === "email" && (
              <>
                {" "}(modo email{preview?.to ? <> · destinatario <b className="font-mono">{preview.to}</b></> : <> · <span className="text-rose-600">sin destinatario</span></>})
              </>
            )}
          </div>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={5}
            data-testid="cita-card-textarea"
            className="w-full text-[13px] font-mono border border-border rounded-md px-3 py-2 bg-white"
          />
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={copyToClipboard}
              disabled={!body}
              className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs px-4 py-2 rounded-md font-semibold inline-flex items-center gap-1.5"
              data-testid="cita-card-copy"
            >
              {copied ? <Check className="w-3.5 h-3.5" weight="bold" /> : <Copy className="w-3.5 h-3.5" weight="bold" />}
              {copied ? "Copiado" : "Copiar al portapapeles"}
            </button>
            {preview?.mode === "email" && (
              <button
                type="button"
                onClick={sendEmail}
                disabled={sending || !preview?.to}
                title={!preview?.to ? "Configura el email destino en Aseguradoras → Notif. citas" : "Enviar email a la aseguradora"}
                className="bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white text-xs px-4 py-2 rounded-md font-semibold inline-flex items-center gap-1.5"
                data-testid="cita-card-send"
              >
                <PaperPlaneTilt className="w-3.5 h-3.5" weight="bold" />
                {sending ? "Enviando…" : "Enviar email"}
              </button>
            )}
            {body !== (preview?.body || "") && (
              <button
                type="button"
                onClick={() => setBody(preview?.body || "")}
                className="bg-white border border-border text-xs text-muted hover:text-foreground px-3 py-2 rounded-md font-semibold"
                data-testid="cita-card-reset"
              >
                Restablecer al original
              </button>
            )}
          </div>
        </div>
      )}
    </SectionCard>
  );
}
